// #44 ScoreCell, #91 ScoreRow and #96 SectionSummary: the read-only faces of a submitted scorecard.
// ScoreCell is the 24 px number on its tint (5 green · 4 blue · 3 gold · 2 and 1 red; "—" when not assessed).
// ScoreRow is a person row over a labelled row of cells (Respect · Ownership · Opportunity · Together · Service ·
// Skill #n), a <dl> so every number is read with its section. SectionSummary is the interviewer's own submitted
// scorecard: per section its name, the five scale numbers as 24 px circles with the chosen one filled blue, and the
// first line of the evidence.

import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { fullTitle } from "../lib/textOf";
import { VisuallyHidden } from "../lib/VisuallyHidden";
import { Person, type PersonProps } from "../core/Person";
import { SCORECARD_LABELS } from "./labels";
import { RATING_SCALE, type RatingValue, type ScoreValue } from "./RatingScale";

export interface ScoreCellProps {
  /** 1–5, or null / "na" when the section was not assessed (or is not assigned). */
  score: ScoreValue | RatingValue | null;
  className?: string | undefined;
}

export function ScoreCell({ score, className }: ScoreCellProps) {
  if (score === null || score === "na") {
    return (
      <span className={cx("mc-sc", "mc-na", className)}>
        <span aria-hidden="true">—</span>
        <VisuallyHidden>{SCORECARD_LABELS.notAssessed}</VisuallyHidden>
      </span>
    );
  }
  return <span className={cx("mc-sc", `mc-s${score}`, className)}>{score}</span>;
}

export interface ScoreItem {
  /** The section's name: a ROOTS value, a skill, or Skill #n. */
  label: string;
  score: ScoreValue | RatingValue | null;
}

export interface ScoreRowProps {
  /** The interviewer whose scores these are. */
  person: Omit<PersonProps, "size" | "className">;
  scores: readonly ScoreItem[];
  /** Beside the scores: the recommendation pill, a concern flag. */
  right?: ReactNode | undefined;
  className?: string | undefined;
}

export function ScoreRow({ person, scores, right, className }: ScoreRowProps) {
  return (
    <div className={cx("mc-scores", className)}>
      <Person size={24} {...person} />
      <div className="mc-scores-r">
        <dl className="mc-scores-l">
          {scores.map((item, at) => (
            <div key={at} className="mc-scores-i">
              <dt>{item.label}</dt>
              <dd><ScoreCell score={item.score} /></dd>
            </div>
          ))}
        </dl>
        {right ? <div className="mc-scores-x">{right}</div> : null}
      </div>
    </div>
  );
}

export interface SectionSummaryItem {
  label: string;
  rating: RatingValue | null;
  /** The first line of the section's evidence (cut with an ellipsis). */
  evidence?: ReactNode | undefined;
}

export interface SectionSummaryProps {
  sections: readonly SectionSummaryItem[];
  /** Under the rows: the Submitted pill and its time. */
  footer?: ReactNode | undefined;
  className?: string | undefined;
}

export function SectionSummary({ sections, footer, className }: SectionSummaryProps) {
  return (
    <div className={cx("mc-secsum-w", className)}>
      <ul className="mc-secsum">
        {sections.map((section, at) => (
          <li key={at} className="mc-ss">
            <span className="mc-ss-n">{section.label}</span>
            <span className="mc-ss-r">
              <span className="mc-ss-dots" aria-hidden="true">
                {RATING_SCALE.map((n) => <span key={n} className={cx("mc-ss-d", section.rating === n && "mc-on")}>{n}</span>)}
              </span>
              <VisuallyHidden>
                {section.rating === null || section.rating === "na" ? SCORECARD_LABELS.notAssessed : `${SCORECARD_LABELS.rating} ${section.rating}`}
              </VisuallyHidden>
            </span>
            <span className="mc-ss-e" title={fullTitle(section.evidence)}>{section.evidence ?? null}</span>
          </li>
        ))}
      </ul>
      {footer ? <div className="mc-ss-f">{footer}</div> : null}
    </div>
  );
}
